import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, TrendingUp, TrendingDown, Minus, Eye, Download, BarChart3, AlertTriangle, Trash2, Edit3 } from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { openProtectedFile } from '../lib/protectedFiles';
import CameraFileInput from './CameraFileInput';
import ExamForm from './ExamForm';
import { DEFAULT_EXAM_TYPES } from '../lib/examTypes';

const API = process.env.REACT_APP_BACKEND_URL || '';

const handleOpenExamAttachment = async (event, attachment) => {
  event.preventDefault();
  try {
    await openProtectedFile(attachment.file_url, attachment.filename || attachment.legenda);
  } catch (error) {
    toast.error(error.message || 'Não foi possível abrir o arquivo');
  }
};

// Helper function to check if value is outside reference range
const isOutOfRange = (value, reference) => {
  if (!reference || !value) return null;
  
  // Try to parse reference like "10-20" or "< 10" or "> 5" or "10 - 20"
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return null;
  
  // Handle range format: "10-20" or "10 - 20"
  const rangeMatch = reference.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    const min = parseFloat(rangeMatch[1]);
    const max = parseFloat(rangeMatch[2]);
    if (numValue < min) return 'low';
    if (numValue > max) return 'high';
    return 'normal';
  }
  
  // Handle "< X" format
  const lessThanMatch = reference.match(/<\s*(\d+(?:\.\d+)?)/);
  if (lessThanMatch) {
    const max = parseFloat(lessThanMatch[1]);
    if (numValue >= max) return 'high';
    return 'normal';
  }
  
  // Handle "> X" format
  const greaterThanMatch = reference.match(/>\s*(\d+(?:\.\d+)?)/);
  if (greaterThanMatch) {
    const min = parseFloat(greaterThanMatch[1]);
    if (numValue <= min) return 'low';
    return 'normal';
  }
  
  return null;
};

// Count alerts in exam
const countExamAlerts = (exam) => {
  if (!exam.parametros) return 0;
  return exam.parametros.filter(p => {
    const status = isOutOfRange(p.valor, p.referencia);
    return status === 'high' || status === 'low';
  }).length;
};

export default function ExamsTab({ animalId, user, initialExamId = '' }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewExamForm, setShowNewExamForm] = useState(false);
  const [examSubTab, setExamSubTab] = useState('all');
  const [selectedExam, setSelectedExam] = useState(null);
  const [showExamDetail, setShowExamDetail] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [openedInitialExamId, setOpenedInitialExamId] = useState('');
  const [comparisonType, setComparisonType] = useState('');
  
  // Filtros
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchExams = useCallback(async () => {
    if (!animalId) return;
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('tipo_exame', filterType);
      if (filterCategory !== 'all') params.append('categoria', filterCategory);

      const response = await axios.get(`${API}/api/animals/${animalId}/exams?${params.toString()}`);
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
      toast.error('Erro ao carregar exames');
    }
  }, [animalId, filterType, filterCategory]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  useEffect(() => {
    if (!initialExamId || openedInitialExamId === initialExamId || exams.length === 0) return;
    const exam = exams.find((item) => item.id === initialExamId);
    if (exam) {
      setSelectedExam(exam);
      setShowExamDetail(true);
      setExamSubTab('all');
      setOpenedInitialExamId(initialExamId);
    }
  }, [initialExamId, openedInitialExamId, exams]);
  
  const resetForm = () => {
    setEditingExam(null);
  };

  const handleStartEditExam = (exam) => {
    setEditingExam(exam);
    setShowExamDetail(false);
    setShowNewExamForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadPDF = async (examId) => {
    try {
      const response = await axios.get(`${API}/api/exams/${examId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exame_${examId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleDownloadConsolidatedPDF = async () => {
    try {
      const response = await axios.get(`${API}/api/animals/${animalId}/exams/consolidated-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `exames_consolidado_${animalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF consolidado gerado!');
    } catch (error) {
      console.error('Error downloading consolidated PDF:', error);
      toast.error('Erro ao gerar PDF consolidado');
    }
  };

  const handleViewExam = (exam) => {
    setSelectedExam(exam);
    setShowExamDetail(true);
  };

  const handleFileUpload = async (examId, file) => {
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não suportado. Use PDF, JPG, PNG ou WebP.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 10MB');
      return;
    }
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', file.type === 'application/pdf' ? 'pdf' : 'imagem');
    formData.append('legenda', file.name);
    
    try {
      await axios.post(`${API}/api/exams/${examId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Anexo adicionado!');
      fetchExams();
      
      // Refresh selected exam
      if (selectedExam?.id === examId) {
        const response = await axios.get(`${API}/api/exams/${examId}`);
        setSelectedExam(response.data);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erro ao enviar anexo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteAttachment = async (examId, attachment) => {
    if (!attachment?.file_url) return;
    if (!window.confirm('Excluir este anexo do exame?')) return;
    try {
      await axios.delete(`${API}/api/exams/${examId}/attachments`, {
        params: { file_url: attachment.file_url }
      });
      toast.success('Anexo excluído');
      fetchExams();
      if (selectedExam?.id === examId) {
        const response = await axios.get(`${API}/api/exams/${examId}`);
        setSelectedExam(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir anexo');
    }
  };

  const handleDeleteExam = async (exam) => {
    if (!window.confirm(`Cancelar logicamente o exame "${exam.tipo_exame}"? A ação ficará registrada na auditoria e o exame sairá das listagens.`)) return;
    try {
      await axios.delete(`${API}/api/exams/${exam.id}`);
      toast.success('Exame cancelado');
      if (selectedExam?.id === exam.id) {
        setSelectedExam(null);
        setShowExamDetail(false);
      }
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir exame');
    }
  };

  const filteredExams = exams.filter(exam => {
    if (examSubTab === 'internos') return exam.categoria === 'Interno';
    if (examSubTab === 'externos') return exam.categoria === 'Externo';
    return true;
  });

  // Get exams for comparison (same type)
  const getExamsForComparison = (type) => {
    return exams.filter(e => e.tipo_exame === type).sort((a, b) => 
      new Date(a.data_coleta) - new Date(b.data_coleta)
    );
  };

  // Get unique exam types for comparison
  const examTypesInHistory = [...new Set(exams.map(e => e.tipo_exame))];

  const canCreateEdit = hasPermission(user, 'exams.create') || hasPermission(user, 'exams.update');
  const canUpdateExam = hasPermission(user, 'exams.update');
  const canAttach = hasPermission(user, 'exams.attach');
  const canDelete = hasPermission(user, 'records.delete');
  const canDeleteAttachments = ['administrador', 'programador'].includes(user?.role);

  const getTrendIcon = (tendencia) => {
    if (tendencia === '↑') return <TrendingUp className="w-4 h-4 text-red-600" />;
    if (tendencia === '↓') return <TrendingDown className="w-4 h-4 text-blue-600" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  return (
    <div className="space-y-6" data-testid="exams-tab">
      {/* Header com botão Novo Exame */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-emerald-800">Exames Complementares</h2>
          <p className="text-sm text-gray-600">Histórico de exames laboratoriais e de imagem</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {exams.length > 0 && (
            <Button
              onClick={handleDownloadConsolidatedPDF}
              variant="outline"
              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 min-h-[44px]"
              data-testid="download-consolidated-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF Consolidado
            </Button>
          )}
          {canCreateEdit && (
            <Button
              onClick={() => {
                if (showNewExamForm) {
                  resetForm();
                  setShowNewExamForm(false);
                } else {
                  setShowNewExamForm(true);
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[44px]"
              data-testid="new-exam-btn"
            >
              {showNewExamForm ? 'Cancelar' : '+ Novo Exame'}
            </Button>
          )}
        </div>
      </div>

      {/* Formulário de Novo Exame / Edição */}
      {showNewExamForm && (
        <Card className="border-emerald-200" data-testid="new-exam-form">
          <CardHeader className="pb-0">
            <CardTitle className="text-lg text-emerald-800">
              {editingExam ? 'Editar Exame' : 'Novo Exame'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ExamForm
              animalId={animalId}
              user={user}
              editingExam={editingExam}
              existingExams={exams}
              submitLabel={editingExam ? 'Salvar Alterações' : 'Criar Exame'}
              onCancel={() => {
                resetForm();
                setShowNewExamForm(false);
              }}
              onSaved={() => {
                setShowNewExamForm(false);
                resetForm();
                fetchExams();
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Filtrar por Tipo</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type-select">
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {[...new Set([...DEFAULT_EXAM_TYPES, ...examTypesInHistory])].map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filtrar por Categoria</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="filter-category-select">
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="Interno">Interno</SelectItem>
                  <SelectItem value="Externo">Externo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sub-tabs: Todos, Internos, Externos, Comparativo */}
      <Tabs value={examSubTab} onValueChange={setExamSubTab}>
        {/* Mobile: Dropdown */}
        <div className="block md:hidden mb-4">
          <Select value={examSubTab} onValueChange={setExamSubTab}>
            <SelectTrigger className="w-full min-h-[48px]" data-testid="mobile-subtab-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Exames</SelectItem>
              <SelectItem value="internos">Exames Internos</SelectItem>
              <SelectItem value="externos">Exames Externos</SelectItem>
              <SelectItem value="comparativo">Comparativo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: Tabs */}
        <TabsList className="hidden md:grid grid-cols-4 mb-4">
          <TabsTrigger value="all" data-testid="tab-all">Todos</TabsTrigger>
          <TabsTrigger value="internos" data-testid="tab-internos">Internos</TabsTrigger>
          <TabsTrigger value="externos" data-testid="tab-externos">Externos</TabsTrigger>
          <TabsTrigger value="comparativo" data-testid="tab-comparativo">Comparativo</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <ExamsList 
            exams={filteredExams} 
            onDownloadPDF={handleDownloadPDF} 
            onViewExam={handleViewExam}
            onEditExam={handleStartEditExam}
            onFileUpload={handleFileUpload}
            onDeleteExam={handleDeleteExam}
            canEdit={canUpdateExam}
            canAttach={canAttach}
            canDelete={canDelete}
            uploadingFile={uploadingFile}
          />
        </TabsContent>
        
        <TabsContent value="internos">
          <ExamsList 
            exams={filteredExams} 
            onDownloadPDF={handleDownloadPDF} 
            onViewExam={handleViewExam}
            onEditExam={handleStartEditExam}
            onFileUpload={handleFileUpload}
            onDeleteExam={handleDeleteExam}
            canEdit={canUpdateExam}
            canAttach={canAttach}
            canDelete={canDelete}
            uploadingFile={uploadingFile}
          />
        </TabsContent>
        
        <TabsContent value="externos">
          <ExamsList 
            exams={filteredExams} 
            onDownloadPDF={handleDownloadPDF} 
            onViewExam={handleViewExam}
            onEditExam={handleStartEditExam}
            onFileUpload={handleFileUpload}
            onDeleteExam={handleDeleteExam}
            canEdit={canUpdateExam}
            canAttach={canAttach}
            canDelete={canDelete}
            uploadingFile={uploadingFile}
          />
        </TabsContent>

        <TabsContent value="comparativo">
          <ComparisonView 
            examTypes={examTypesInHistory}
            getExamsForComparison={getExamsForComparison}
            comparisonType={comparisonType}
            setComparisonType={setComparisonType}
          />
        </TabsContent>
      </Tabs>

      {/* Modal de Detalhes do Exame */}
      <Dialog open={showExamDetail} onOpenChange={setShowExamDetail}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-800">
              <FileText className="w-5 h-5" />
              Detalhes do Exame
            </DialogTitle>
          </DialogHeader>
          {selectedExam && (
            <ExamDetailView 
              exam={selectedExam} 
              onDownloadPDF={handleDownloadPDF}
              onEditExam={handleStartEditExam}
              onFileUpload={handleFileUpload}
              onDeleteExam={handleDeleteExam}
              onDeleteAttachment={handleDeleteAttachment}
              canEdit={canUpdateExam}
              canAttach={canAttach}
              canDelete={canDelete}
              canDeleteAttachments={canDeleteAttachments}
              uploadingFile={uploadingFile}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Component for listing exams
function ExamsList({ exams, onDownloadPDF, onViewExam, onEditExam, onFileUpload, onDeleteExam, canEdit, canAttach, canDelete, uploadingFile }) {
  if (exams.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Nenhum exame registrado</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="exams-list">
      {exams.map((exam) => {
        const alertCount = countExamAlerts(exam);
        
        return (
        <Card 
          key={exam.id} 
          className={`border-l-4 ${alertCount > 0 ? 'border-l-red-500' : 'border-l-emerald-600'}`} 
          data-testid={`exam-card-${exam.id}`}
        >
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <div className="flex-1">
                <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-emerald-800">{exam.tipo_exame}</h3>
                      {alertCount > 0 && (
                        <Badge className="bg-red-100 text-red-700 border-red-300 flex items-center gap-1" data-testid="alert-badge">
                          <AlertTriangle className="w-3 h-3" />
                          {alertCount} alerta{alertCount > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge className={
                        exam.categoria === 'Interno' 
                          ? 'bg-blue-100 text-blue-800 border-blue-300' 
                          : 'bg-purple-100 text-purple-800 border-purple-300'
                      }>
                        {exam.categoria}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        {exam.realizado_em}
                      </span>
                      {exam.anexos?.length > 0 && (
                        <Badge variant="outline" className="text-gray-600">
                          {exam.anexos.length} anexo(s)
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mt-3">
                  <p><strong>Data Coleta:</strong> {new Date(exam.data_coleta).toLocaleDateString('pt-BR')}</p>
                  {exam.data_resultado && (
                    <p><strong>Data Resultado:</strong> {new Date(exam.data_resultado).toLocaleDateString('pt-BR')}</p>
                  )}
                  <p><strong>Responsável:</strong> {exam.responsavel}</p>
                  {exam.laboratorio_origem && (
                    <p><strong>Laboratório:</strong> {exam.laboratorio_origem}</p>
                  )}
                </div>
                
                {exam.parametros && exam.parametros.length > 0 && (
                  <div className="mt-4 border-t pt-3">
                    <p className="font-semibold text-sm mb-2">Resultados ({exam.parametros.length}):</p>
                    <div className="grid grid-cols-1 gap-2">
                      {exam.parametros.slice(0, 3).map((param, idx) => {
                        const rangeStatus = isOutOfRange(param.valor, param.referencia);
                        const isAlert = rangeStatus === 'high' || rangeStatus === 'low';
                        
                        return (
                        <div 
                          key={idx} 
                          className={`flex items-center justify-between p-2 rounded text-sm ${
                            isAlert ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {isAlert && <AlertTriangle className="w-4 h-4 text-red-500" />}
                            <strong className={isAlert ? 'text-red-700' : ''}>{param.nome_parametro}:</strong> 
                            <span className={isAlert ? 'text-red-700 font-semibold' : ''}>
                              {param.valor} {param.unidade}
                            </span>
                            {param.resultado && (
                              <Badge className={param.resultado === 'Positivo' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                                {param.resultado}
                              </Badge>
                            )}
                            {param.referencia && (
                              <span className="text-xs text-gray-500">(Ref: {param.referencia})</span>
                            )}
                            {(param.intensidade || param.agente_identificado) && (
                              <span className="text-xs text-gray-500">
                                {[param.intensidade, param.agente_identificado].filter(Boolean).join(' • ')}
                              </span>
                            )}
                          </span>
                          <div className="flex items-center gap-2">
                            {param.tendencia && (
                              <span className={`text-lg ${
                                param.tendencia === '↑' ? 'text-red-600' : 
                                param.tendencia === '↓' ? 'text-blue-600' : 
                                'text-gray-600'
                              }`}>
                                {param.tendencia}
                              </span>
                            )}
                            {param.valor_anterior && (
                              <span className="text-xs text-gray-500">
                                (Ant: {param.valor_anterior})
                              </span>
                            )}
                          </div>
                        </div>
                        );
                      })}
                      {exam.parametros.length > 3 && (
                        <p className="text-sm text-emerald-600 cursor-pointer" onClick={() => onViewExam(exam)}>
                          + {exam.parametros.length - 3} parâmetros...
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {exam.observacoes && (
                  <div className="mt-3 p-2 bg-yellow-50 rounded text-sm">
                    <strong>Obs:</strong> {exam.observacoes}
                  </div>
                )}
              </div>
              
              <div className="flex flex-col gap-2 sm:flex-row md:flex-col">
                <Button
                  onClick={() => onViewExam(exam)}
                  variant="outline"
                  size="sm"
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 min-h-[40px]"
                  data-testid={`view-exam-btn-${exam.id}`}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Ver
                </Button>
                <Button
                  onClick={() => onDownloadPDF(exam.id)}
                  variant="outline"
                  size="sm"
                  className="border-gray-400 text-gray-600 hover:bg-gray-50 min-h-[40px]"
                  data-testid={`download-pdf-btn-${exam.id}`}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  PDF
                </Button>
                {canEdit && (
                  <Button
                    type="button"
                    onClick={() => onEditExam(exam)}
                    variant="outline"
                    size="sm"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50 min-h-[40px]"
                    data-testid={`edit-exam-btn-${exam.id}`}
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                )}
                {canAttach && (
                  <CameraFileInput
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    disabled={uploadingFile}
                    onFileSelected={(file) => onFileUpload(exam.id, file)}
                    fileLabel={uploadingFile ? '...' : 'Anexar'}
                    cameraLabel="Foto"
                  />
                )}
                {canDelete && (
                  <Button
                    type="button"
                    onClick={() => onDeleteExam(exam)}
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-600 hover:bg-red-50 min-h-[40px]"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}

// Detailed exam view component
function ExamDetailView({ exam, onDownloadPDF, onEditExam, onFileUpload, onDeleteExam, onDeleteAttachment, canEdit, canAttach, canDelete, canDeleteAttachments, uploadingFile }) {
  return (
    <div className="space-y-4">
      {/* Header Info */}
      <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
        <div>
          <p className="text-gray-500">Tipo</p>
          <p className="font-semibold">{exam.tipo_exame}</p>
        </div>
        <div>
          <p className="text-gray-500">Categoria</p>
          <Badge className={
            exam.categoria === 'Interno' 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-purple-100 text-purple-800'
          }>
            {exam.categoria}
          </Badge>
        </div>
        <div>
          <p className="text-gray-500">Data Coleta</p>
          <p className="font-semibold">{new Date(exam.data_coleta).toLocaleString('pt-BR')}</p>
        </div>
        <div>
          <p className="text-gray-500">Data Resultado</p>
          <p className="font-semibold">
            {exam.data_resultado ? new Date(exam.data_resultado).toLocaleString('pt-BR') : '-'}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Responsável</p>
          <p className="font-semibold">{exam.responsavel}</p>
        </div>
        <div>
          <p className="text-gray-500">Realizado em</p>
          <p className="font-semibold">{exam.realizado_em}</p>
        </div>
        {exam.laboratorio_origem && (
          <div className="col-span-2">
            <p className="text-gray-500">Laboratório</p>
            <p className="font-semibold">{exam.laboratorio_origem}</p>
          </div>
        )}
      </div>

      {/* Parameters */}
      {exam.parametros && exam.parametros.length > 0 && (
        <div className="border-t pt-4">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h4 className="font-semibold text-emerald-800">Resultados Estruturados</h4>
            {countExamAlerts(exam) > 0 && (
              <Badge className="bg-red-100 text-red-700 border-red-300 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {countExamAlerts(exam)} valor(es) fora da referência
              </Badge>
            )}
          </div>
          <div className="space-y-2">
            {exam.parametros.map((param, idx) => {
              const rangeStatus = isOutOfRange(param.valor, param.referencia);
              const isAlert = rangeStatus === 'high' || rangeStatus === 'low';
              
              return (
              <div 
                key={idx} 
                className={`flex flex-col gap-3 p-3 rounded sm:flex-row sm:items-center sm:justify-between ${
                  isAlert ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isAlert && <AlertTriangle className="w-5 h-5 text-red-500" />}
                  <div>
                    <p className={`font-medium ${isAlert ? 'text-red-700' : ''}`}>{param.nome_parametro}</p>
                    <p className="text-sm text-gray-500">
                      {param.referencia && `Ref: ${param.referencia}`}
                      {isAlert && (
                        <span className="ml-2 text-red-600 font-medium">
                          ({rangeStatus === 'high' ? 'ALTO' : 'BAIXO'})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {param.resultado && (
                    <Badge className={param.resultado === 'Positivo' ? 'mb-1 bg-red-100 text-red-700' : 'mb-1 bg-emerald-100 text-emerald-700'}>
                      {param.resultado}
                    </Badge>
                  )}
                  <p className={`font-bold text-lg ${isAlert ? 'text-red-700' : ''}`}>
                    {param.valor} <span className="text-sm font-normal">{param.unidade}</span>
                  </p>
                  {(param.intensidade || param.agente_identificado) && (
                    <p className="text-xs text-gray-600">
                      {[param.intensidade, param.agente_identificado].filter(Boolean).join(' • ')}
                    </p>
                  )}
                  {param.valor_anterior && (
                    <p className="text-xs text-gray-500 flex items-center justify-end gap-1">
                      Anterior: {param.valor_anterior}
                      {param.tendencia && (
                        <span className={
                          param.tendencia === '↑' ? 'text-red-600' : 
                          param.tendencia === '↓' ? 'text-blue-600' : ''
                        }>
                          {param.tendencia}
                        </span>
                      )}
                    </p>
                  )}
                  {param.observacoes && (
                    <p className="mt-1 text-xs text-gray-500">{param.observacoes}</p>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Observations */}
      {exam.observacoes && (
        <div className="border-t pt-4">
          <h4 className="font-semibold text-emerald-800 mb-2">Observações</h4>
          <p className="text-sm bg-yellow-50 p-3 rounded">{exam.observacoes}</p>
        </div>
      )}

      {/* Attachments */}
      <div className="border-t pt-4">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h4 className="font-semibold text-emerald-800">Anexos</h4>
          {canAttach && (
            <CameraFileInput
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              disabled={uploadingFile}
              onFileSelected={(file) => onFileUpload(exam.id, file)}
              fileLabel={uploadingFile ? 'Enviando...' : 'Adicionar arquivo'}
              cameraLabel="Tirar foto"
            />
          )}
        </div>
        
        {exam.anexos && exam.anexos.length > 0 ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {exam.anexos.map((anexo, idx) => (
              <div
                key={anexo.file_url || idx}
                className="flex items-center justify-between gap-2 p-2 border rounded hover:bg-gray-50"
              >
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${anexo.file_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 flex-1 items-center gap-2"
                  onClick={(event) => handleOpenExamAttachment(event, anexo)}
                >
                  <FileText className="w-4 h-4 flex-shrink-0 text-gray-500" />
                  <span className="text-sm truncate">{anexo.filename || anexo.legenda}</span>
                </a>
                {canDeleteAttachments && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-red-600"
                    onClick={() => onDeleteAttachment?.(exam.id, anexo)}
                    title="Excluir anexo"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Nenhum anexo</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row">
        <Button onClick={() => onDownloadPDF(exam.id)} className="w-full bg-emerald-600 sm:w-auto">
          <Download className="w-4 h-4 mr-2" />
          Baixar PDF
        </Button>
        {canEdit && (
          <Button onClick={() => onEditExam(exam)} variant="outline" className="w-full border-blue-300 text-blue-700 hover:bg-blue-50 sm:w-auto">
            <Edit3 className="w-4 h-4 mr-2" />
            Editar Exame
          </Button>
        )}
        {canDelete && (
          <Button onClick={() => onDeleteExam(exam)} variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50 sm:w-auto">
            <Trash2 className="w-4 h-4 mr-2" />
            Cancelar Exame
          </Button>
        )}
      </div>
    </div>
  );
}

// Comparison view component
function ComparisonView({ examTypes, getExamsForComparison, comparisonType, setComparisonType }) {
  const selectedExams = comparisonType ? getExamsForComparison(comparisonType) : [];

  // Get all unique parameters across exams
  const allParams = new Set();
  selectedExams.forEach(exam => {
    exam.parametros?.forEach(p => allParams.add(p.nome_parametro));
  });
  const paramsList = Array.from(allParams);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-emerald-800">
          <BarChart3 className="w-5 h-5" />
          Comparativo de Exames
        </CardTitle>
      </CardHeader>
      <CardContent>
        {examTypes.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            Nenhum exame registrado para comparação
          </p>
        ) : (
          <>
            <div className="mb-4">
              <Label>Selecione o tipo de exame para comparar</Label>
              <Select value={comparisonType} onValueChange={setComparisonType}>
                <SelectTrigger data-testid="comparison-type-select">
                  <SelectValue placeholder="Selecione um tipo" />
                </SelectTrigger>
                <SelectContent>
                  {examTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {comparisonType && selectedExams.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-emerald-50">
                      <th className="border p-2 text-left font-semibold">Parâmetro</th>
                      {selectedExams.map((exam, idx) => (
                        <th key={idx} className="border p-2 text-center font-semibold min-w-[120px]">
                          {new Date(exam.data_coleta).toLocaleDateString('pt-BR')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paramsList.map((paramName, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border p-2 font-medium">{paramName}</td>
                        {selectedExams.map((exam, examIdx) => {
                          const param = exam.parametros?.find(p => p.nome_parametro === paramName);
                          return (
                            <td key={examIdx} className="border p-2 text-center">
                              {param ? (
                                <span className="flex items-center justify-center gap-1">
                                  {param.valor} {param.unidade}
                                  {param.tendencia && (
                                    <span className={
                                      param.tendencia === '↑' ? 'text-red-600' : 
                                      param.tendencia === '↓' ? 'text-blue-600' : ''
                                    }>
                                      {param.tendencia}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {selectedExams.length === 1 && (
                  <p className="text-center text-gray-500 mt-4 text-sm">
                    Adicione mais exames do mesmo tipo para comparação
                  </p>
                )}
              </div>
            )}

            {comparisonType && selectedExams.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                Nenhum exame encontrado para este tipo
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
